import BillingStore from 'billing/BillingStore'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { AlertFactory } from 'alerts/Alert'
import { SelectPaymentMethodDelegate } from 'billing/payment-methods/PaymentMethodsRootViewModel'
import DocumentAccessStore from 'documents/DocumentAccessStore'
import DocumentStore from 'documents/DocumentStore'
import DocumentTypeStore from 'documents/DocumentTypeStore'
import DocumentVersionStore from 'documents/DocumentVersionStore'
import { ViewDocumentDetailsDelegate } from 'documents/views/DocumentRoot/DocumentDetailsRootViewModel'
import { FindMembers, PrimeMemberByPublicId } from 'iam/MemberStore'
import { GetCurrentMember } from 'iam/SessionStore'
import {
  FetchTeamMember,
  FetchUserTeamMembers,
  FilterTeamMembers,
  FindTeamMembers,
  FindUserTeamMembersByUserPublicId,
} from 'iam/TeamMemberStore'
import link from 'infra/link'
import Job from 'jobs/Job'
import { JobClosingFlow } from 'jobs/JobClosingFlow'
import { JobDocumentsAdapter } from 'jobs/JobDocumentsViewModelAdapter'
import JobStore from 'jobs/JobStore'
import MilestoneStore from 'jobs/MilestoneStore'
import QuestionStore, { QuestionsQuerySession } from 'jobs/QuestionStore'
import { ConversationViewModelFactory } from 'messaging/components/ConversationView/ConversationViewModel'
import {
  MessageNotificationsStream,
  MessageSystemNotification,
} from 'messaging/MessageNotifications'
import { action, observable, makeObservable, computed } from 'mobx'
import { task } from 'mobx-task'
import DesktopNotificationStore from 'notifications/DesktopNotificationStore'
import { Observable, Subject } from 'rxjs'
import { map } from 'rxjs/operators'
import { Shortcuts } from 'shortcuts/Shortcuts'
import { Id, PageActivity, Router } from 'types/definitions'
import { toStream } from 'utils/mobx-util'
import CloseJobViewModel from '../CloseJobDialog/CloseJobAsCollaboratorDialog/CloseJobViewModel'
import JobViewModel from './Job/JobViewModel'
import { JobListViewModel } from './JobList/JobListViewModel'
import { MessageBankDialogViewModelFactory } from 'messaging/message-bank/MessageBankDialog/MessageBankDialogViewModel'
import SureprepStore from 'sureprep/SureprepStore'
import PublishedLegendStore from 'legends/PublishedLegendStore'
import UserDataStore from 'iam/UserDataStore'
import JobProgressionStore from 'jobs/progression/JobProgressionStore'
import JobSearchAdapter from '../../infra/JobSearchAdapter'
import JobSearchStore from '../JobManagement/JobSearchStore'
import { FilterStorage } from '../../../infra/FiltersViewModel'
import { localStorage } from '../../../infra/storage'
import { EnsureTermsAccepted } from 'iam/views/TermsModal/TermsModalViewModel'
import ConversationStore from 'messaging/ConversationStore'
import ProviderStore from 'jobs/ProviderStore'
import { JobTicketStore } from '../../tickets/JobTicketStore'
import { JobNoteStore } from '../../notes/JobNoteStore'
import RelationManagerStore from 'csdesk/RelationManagerStore'
import { InsightStore } from '../../insights/InsightStore'
import CouponStore from 'billing/CouponStore'
import InventoryStore from 'admin/InventoryStore'
import TeamStore from '../../../iam/TeamStore'
import { UserDataType } from '../../../iam/models/UserDataType'
import InfoGatheringStore from '../../InfoGatheringStore'
import CreateJobDialogViewModel from 'jobs/creation/views/JobCreationDialog/JobCreationDialogViewModel'
import JobRatingsStore from '../../ratings/JobRatingStore'
import BookkeepingProgressionStore from 'jobs/bookkeeping/BookkeepingProgressionStore'
import ConsultationStore from '../../consultations/ConsultationStore'
import { SupportingItemsPorts } from 'admin/ports'
import DocumentRequestStore from 'jobs/document-requests/DocumentRequestStore'
import RoomStore from '../../../messaging/rooms/RoomStore'
import BusinessProfileStore from 'jobs/info-gathering/business-info/BusinessProfileStore'
import IndividualProfileStore from 'jobs/info-gathering/individual-info/IndividualProfileStore'
import TaxRefundMethodStore from 'jobs/info-gathering/tax-refund-method/TaxRefundMethodStore'
import AvailableJobProgressionFlowsStore from 'jobs/progression/AvailableJobProgressionFlows/AvailableJobProgressionFlowsStore'
import WorkspaceSelectorState from 'iam/views/AuthenticationContainer/WorkspaceSelector/WorkspaceSelectorState'
import { DocumentManualVerificationViewModel } from 'jobs/views/JobRoot/Job/DocumentManualVerificationModal/DocumentManualVerificationViewModel'
import TxfJobStore from 'jobs/TxfJobStore'
import {
  AddToCalendarDelegate,
  ExternalUrlDelegate,
} from 'shell/platform/types'

export type IsFirmAdminInWorkspace = (workspaceId: Id) => boolean

/**
 * Responsible for managing the current Job view model as well
 * as the job list.
 */
export default class JobRootViewModel {
  /**
   * Map of Job ID to controller instance.
   */
  private jobVMs = new Map<string, JobViewModel>()

  /**
   * The current details view model.
   */
  @observable private current: JobViewModel | null = null

  /**
   * The current Close Job dialog, if any.
   */
  @observable closeJobDialog: CloseJobViewModel | null = null

  /*
   * Whether the user can edit draft jobs.
   */
  @computed
  get canEditDraftJobAsAdmin() {
    return this.getCurrentUserWorkspaceMemberships().some((x) =>
      x.member.hasPermission('ADMIN_JOB_CREATE'),
    )
  }

  /**
   * Whether the user can update a job as an admin.
   */
  @computed
  get canUpdateJobAsAdmin() {
    return this.getCurrentUserWorkspaceMemberships().some((m) =>
      m.member.hasPermission('ADMIN_JOB_UPDATE'),
    )
  }

  /**
   * Determinate if the current member is a firm admin in the workspace stored in the session.
   */
  @computed
  get isFirmAdmin() {
    // Since we don't pass a workspace id, it will take the one in the session.
    return this.isFirmAdminInWorkspace()
  }

  /**
   * Determinate if the current member is a Pro by checking the user type.
   */
  @computed
  get isPro() {
    const currentMember = this.getCurrentMember()
    const userData = this.userDataStore.getByPublicId(
      currentMember.user.publicId,
    )

    return userData?.userDataType === UserDataType.Pro
  }

  @computed
  get useEditDraftJobForFirmAdmin() {
    return this.isFirmAdmin
  }

  /**
   * Subject for requesting to show the Close Job dialog.
   */
  requestClose$ = new Subject<Job>()

  searchServiceItems = (
    workspaceIds: Id[],
    limit: number,
    searchText?: string,
    cursor?: string,
  ) =>
    this.inventoryStore.fetchInventoryItems(
      workspaceIds,
      limit,
      searchText,
      cursor,
    )

  /**
   * Job switcher.
   */
  jobListVM = new JobListViewModel(
    new JobSearchAdapter(this.jobSearchStore),
    this.jobTicketStore,
    this.newAlert,
    this.fetchUserTeamMembers,
    this.publishedLegendStore.findLegends,
    () => this.current && this.current.job,
    this.getCurrentUserWorkspaceMemberships,
    this.probeCanViewOpportunities,
    this.getCurrentMember,
    this.userDataStore,
    (ns) => new FilterStorage(localStorage, ns),
    this.filterTeamMembers,
    () => this.canEditDraftJobAsAdmin,
    this.jobSearchStore,
    (params) => this.teamStore.getUserTeams(params),
    this.searchServiceItems,
    this.workspaceSelectorState,
    () => this.documentTypeStore.fetchTypes(),
  )

  /**
   * Message system notifications
   */
  messageNotifications$ = MessageNotificationsStream(
    // Represents the currently active conversation.
    toStream(() => this.current, true).pipe(
      map((c) => c && c.job.conversation),
    ),
    this.conversationStore.newMessages$,
    this.pageActivity$,
  ).pipe(
    map(
      (message): MessageSystemNotification => ({
        message,
        onClick: () => {
          if (message.conversation.meta.type === 'Job') {
            this.router.push(link.job(message.conversation.meta.id))
          }
        },
      }),
    ),
  )

  /**
   * Constructor.
   */
  constructor(
    private router: Router,
    private newAlert: AlertFactory,
    private jobStore: JobStore,
    private consultationStore: ConsultationStore,
    private jobTicketStore: JobTicketStore,
    private jobNoteStore: JobNoteStore,
    private jobInsightStore: InsightStore,
    private progressionStore: JobProgressionStore,
    private availableJobProgressionFlowsStore: AvailableJobProgressionFlowsStore,
    private desktopNotificationStore: DesktopNotificationStore,
    private milestoneStore: MilestoneStore,
    private createQuestionsQuerySession: (id: Id) => QuestionsQuerySession,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private findTeamMembersByUserPublicId: FindUserTeamMembersByUserPublicId,
    private fetchTeamMember: FetchTeamMember,
    private fetchUserTeamMembers: FetchUserTeamMembers,
    private findMembers: FindMembers,
    private findTeamMembers: FindTeamMembers,
    private providerStore: ProviderStore,
    private createConversationViewModel: ConversationViewModelFactory,
    private documentStore: DocumentStore,
    private documentVersionStore: DocumentVersionStore,
    private documentTypeStore: DocumentTypeStore,
    private documentAccessStore: DocumentAccessStore,
    private shortcuts: Shortcuts,
    private pageActivity$: Observable<PageActivity>,
    private getCurrentMember: GetCurrentMember,
    private probeCanViewOpportunities: () => Promise<boolean>,
    private viewDocumentDetails: ViewDocumentDetailsDelegate,
    private selectPaymentMethod: SelectPaymentMethodDelegate,
    private jobDocumentsAdapter: JobDocumentsAdapter,
    private createMessageBankDialogViewModel: MessageBankDialogViewModelFactory,
    private sureprepStore: SureprepStore,
    private billingStore: BillingStore,
    private publishedLegendStore: PublishedLegendStore,
    private userDataStore: UserDataStore,
    private jobSearchStore: JobSearchStore,
    private ensureTermsAccepted: EnsureTermsAccepted,
    private conversationStore: ConversationStore,
    private filterTeamMembers: FilterTeamMembers,
    private couponStore: CouponStore,
    private inventoryStore: InventoryStore,
    private questionStore: QuestionStore,
    private teamStore: TeamStore,
    private infoGatheringStore: InfoGatheringStore,
    public jobCreationDialogViewModel: CreateJobDialogViewModel,
    private jobRatingsStore: JobRatingsStore,
    private bookkeepingProgressionStore: BookkeepingProgressionStore,
    private supportingItemsAdapter: SupportingItemsPorts,
    private documentRequestStore: DocumentRequestStore,
    private roomStore: RoomStore,
    private individualProfileStore: IndividualProfileStore,
    private businessProfileStore: BusinessProfileStore,
    private taxRefundMethodStore: TaxRefundMethodStore,
    private workspaceSelectorState: WorkspaceSelectorState,
    private documentManualVerificationVM: DocumentManualVerificationViewModel,
    private txfJobStore: TxfJobStore,
    private getAddToCalendarDelegate: () => AddToCalendarDelegate,
    private getExternalUrlDelegate: () => ExternalUrlDelegate,
    private relationManagerStore: RelationManagerStore,
    private primeMemberByPublicId: PrimeMemberByPublicId,
  ) {
    makeObservable(this)
    JobClosingFlow(this.requestClose$).subscribe(
      action((job: Job) => {
        // Whenever we get a request to close a job, _or_ to rate it,
        // create an instance of the Close Job view model and show it.
        this.closeJobDialog = new CloseJobViewModel(job, this.jobStore)
        this.closeJobDialog.show()
      }),
    )
  }

  /**
   * Activates the root controller.
   */
  activate = task.resolved(async () => {
    // Intentionally not waiting.
    this.jobListVM.activate().then()
    this.documentTypeStore.fetchTypes()
    this.userDataStore.fetchByPublicId(
      Id.assert(this.getCurrentMember().user.publicId),
    )
  })

  /**
   * Sets the current details view model instance, or disposes the current one if
   * `vm` is `null`.
   *
   * @param vm
   */
  @action.bound
  setDetails(vm: JobViewModel | null) {
    if (this.current) {
      this.current.deactivate()
    }
    this.current = vm
    if (vm) {
      vm.activate()
    }
  }

  /**
   * Determinate if the current member is a firm admin in the given workspace
   * by checking the user email, user type and workspace type.
   *
   * @param workspaceId
   * @returns
   */
  isFirmAdminInWorkspace(workspaceId?: Id) {
    const member = this.getCurrentMember(workspaceId)
    const userData = this.userDataStore.getByPublicId(member.user.publicId)

    const invalidUserEmailDomains = [
      '@taxfyle.com',
      '@worklayer.com',
      '@mytps.com',
    ]

    return (
      userData?.userDataType === UserDataType.User &&
      member.workspace.classification === 'B2B' &&
      !invalidUserEmailDomains.some((domain) =>
        member.user.email.includes(domain),
      )
    )
  }

  /**
   * Fetches the job and creates a controller for it.
   */
  viewJob = task(async (id: Id) => {
    id = Id.assert(id)
    // If we are requesting to view the same job as we are currently
    // viewing, don't do anything.
    if (this.current && this.current.job.id === id) {
      return this.current
    }

    const existing = this.jobVMs.get(id)
    if (existing) {
      this.setDetails(existing)
      return existing
    }

    const job = await this.jobStore.fetchJob(id)
    const details = this.createJobViewModel(job)

    this.setDetails(details)
    this.jobVMs.set(id, details)
    return details
  })

  /**
   * Called when the view model is no longer actively being used.
   */
  deactivate() {
    this.setDetails(null)
    this.jobListVM.deactivate()
  }

  /**
   * Creates a Job View Model.
   *
   * @param job
   */
  private createJobViewModel(job: Job) {
    return new JobViewModel(
      this.router,
      this.newAlert,
      this.jobStore,
      this.consultationStore,
      this.jobTicketStore,
      this.jobNoteStore,
      this.jobInsightStore,
      this.progressionStore,
      this.availableJobProgressionFlowsStore,
      this.desktopNotificationStore,
      this.milestoneStore,
      this.documentStore,
      this.billingStore,
      this.documentVersionStore,
      this.documentAccessStore,
      () => this.createQuestionsQuerySession(job.id),
      this.findTeamMembersByUserPublicId,
      this.fetchTeamMember,
      this.findMembers,
      this.findTeamMembers,
      this.providerStore,
      this.createConversationViewModel,
      this.shortcuts,
      this.requestClose$.next.bind(this.requestClose$),
      job,
      this.getCurrentMember,
      this.viewDocumentDetails,
      this.selectPaymentMethod,
      this.jobDocumentsAdapter,
      this.createMessageBankDialogViewModel,
      this.sureprepStore,
      this.ensureTermsAccepted,
      this.conversationStore,
      this.couponStore,
      this.inventoryStore,
      () => this.canEditDraftJobAsAdmin,
      this.questionStore,
      this.teamStore,
      this.infoGatheringStore,
      this.jobCreationDialogViewModel,
      this.userDataStore.getByPublicId,
      this.canUpdateJobAsAdmin,
      this.jobRatingsStore,
      this.isPro,
      this.bookkeepingProgressionStore,
      (workspaceId?: Id) => this.isFirmAdminInWorkspace(workspaceId),
      this.supportingItemsAdapter,
      this.documentRequestStore,
      this.roomStore,
      this.individualProfileStore,
      this.businessProfileStore,
      this.taxRefundMethodStore,
      this.documentManualVerificationVM,
      this.txfJobStore,
      this.getAddToCalendarDelegate,
      this.getExternalUrlDelegate,
      this.relationManagerStore,
      this.primeMemberByPublicId,
    )
  }
}
